"use client";
import { useState, useTransition } from "react";

import { useRouter } from "@/i18n/navigation";
import { IContact } from "@/interfaces/contact-model";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { useParams } from "next/navigation";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FaEnvelope, FaPhone } from "react-icons/fa";
import { HiOutlineLocationMarker } from "react-icons/hi";
import { toast } from "sonner";

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  mobile: "",
  message: "",
};

interface pageProps {
  data: IContact;
}

const ContactContent = ({ data }: pageProps) => {
  const { locale } = useParams();
  const t = useTranslations("contactPage");
  const [userData, setUserData] = useState(initialValues);
  const [isPending, startTransition] = useTransition();
  const router = useRouter();

  const handleFormSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    startTransition(async () => {
      try {
        const response = await fetch("/api/v1/contact", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(userData),
        });
        const res = await response.json();
        if (!response.ok) {
          toast.error(res.error);
          return;
        }
        toast.success(
          "Thanks For Your Submission! We will get back to you soon. Redirecting to home page..."
        );
        setUserData(initialValues);
        router.replace("/");
      } catch (error) {
        console.log(error);
        toast.error("Something went wrong! Please try again later.");
      }
    });
  };

  let address = `${data?.address_details?.address_line}, ${data?.address_details?.city}, ${data?.address_details?.country}`;

  return (
    <section className="w-full h-full my-12 md:my-24">
      <div className="container px-5 mx-auto w-full h-full">
        <div className="w-full h-full grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-8">
          <article className="bg-gray-800 rounded-2xl p-10">
            <div className="relative w-24 h-24">
              <Image
                src="/assets/images/private_jet.svg"
                alt="private jet image"
                fill
                className="object-cover"
              />
            </div>
            <div className="w-[180px] h-[1px] bg-gray-600 -mt-5"></div>
            <h3 className="mt-8 text-white text-4xl font-normal uppercase">
              private fleet services
            </h3>
            <div className="mt-6 flex flex-col gap-5 text-white">
              <Link href="/" className="flex items-center gap-4">
                <span className="flex justify-center items-center text-xl">
                  <FaEnvelope />
                </span>
                {data?.contact_details?.email}
              </Link>
              <Link href="/" className="flex items-center gap-4">
                <span className="flex justify-center items-center text-xl">
                  <FaPhone />
                </span>
                {data?.contact_details?.phone_number}
              </Link>
              <p className="flex items-center gap-4">
                <span className="flex justify-center items-center text-xl">
                  <HiOutlineLocationMarker />
                </span>
                {address}
              </p>
            </div>
          </article>
          <form
            className="bg-white/90 rounded-2xl shadow-sm backdrop-blur-sm py-8 px-4 md:p-10 flex flex-col gap-6 border border-gray-100"
            onSubmit={handleFormSubmit}
          >
            <div className="flex flex-col gap-2">
              <h3 className="text-2xl font-medium text-gray-800">
                {locale === "en" ? "Get In Touch" : "اتصل بنا"}
              </h3>
              <p className="mb-4 text-gray-600">
                {locale === "en"
                  ? "Please fill out the form below to get in touch with us. Our team will get back to you shortly."
                  : "يرجى إعداد النموذج أدناه للتواصل معنا. سيتم الاتصال بك في وقت قريب."}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <label htmlFor="firstName" className="text-gray-800">
                  {t("fName")}
                </label>
                <input
                  type="text"
                  id="firstName"
                  autoFocus
                  value={userData.firstName}
                  disabled={isPending}
                  onChange={(e) =>
                    setUserData({
                      ...userData,
                      firstName: e.target.value,
                    })
                  }
                  required
                  className="px-4 py-2 w-full rounded border border-black/20 bg-white resize-none min-h-[40px] focus:outline-none focus:ring-2 focus:ring-[#5a558e] focus:border-[#5a558e] disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="lastName" className="text-gray-800">
                  {t("lName")}
                </label>
                <input
                  type="text"
                  id="lastName"
                  disabled={isPending}
                  value={userData.lastName}
                  onChange={(e) =>
                    setUserData({
                      ...userData,
                      lastName: e.target.value,
                    })
                  }
                  required
                  className="px-4 py-2 w-full rounded border border-black/20 bg-white resize-none min-h-[40px] focus:outline-none focus:ring-2 focus:ring-[#5a558e] focus:border-[#5a558e] disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <label htmlFor="email" className="text-gray-800">
                  {t("email")}
                </label>
                <input
                  type="email"
                  disabled={isPending}
                  id="email"
                  value={userData.email}
                  onChange={(e) =>
                    setUserData({
                      ...userData,
                      email: e.target.value,
                    })
                  }
                  required
                  className="px-4 py-2 w-full rounded border border-black/20 bg-white resize-none min-h-[40px] focus:outline-none focus:ring-2 focus:ring-[#5a558e] focus:border-[#5a558e] disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="mobile" className="text-gray-800">
                  {t("mobile")}
                </label>
                <input
                  type="tel"
                  id="mobile"
                  disabled={isPending}
                  value={userData.mobile}
                  onChange={(e) =>
                    setUserData({
                      ...userData,
                      mobile: e.target.value,
                    })
                  }
                  required
                  className="px-4 py-2 w-full rounded border border-black/20 bg-white resize-none min-h-[40px] focus:outline-none focus:ring-2 focus:ring-[#5a558e] focus:border-[#5a558e] disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="message" className="text-gray-800">
                {t("message")}
              </label>
              <textarea
                name="message"
                id="message"
                cols={30}
                rows={10}
                disabled={isPending}
                value={userData.message}
                onChange={(e) =>
                  setUserData({
                    ...userData,
                    message: e.target.value,
                  })
                }
                required
                className="px-4 py-2 w-full rounded border border-black/20 bg-white resize-none min-h-[40px] focus:outline-primary disabled:opacity-50 disabled:cursor-not-allowed"
              ></textarea>
            </div>
            <button
              type="submit"
              disabled={isPending}
              className="self-start md:self-center flex items-center justify-center gap-2 bg-[#5a558e] text-white rounded-lg px-15 py-4 uppercase text-base font-normal leading-6 whitespace-nowrap transition-all duration-200 ease-in-out  hover:bg-[#5a558e]/90 w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending && (
                <AiOutlineLoading3Quarters className="w-4 h-4 animate-spin" />
              )}
              {isPending ? "Please wait..." : t("submit")}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactContent;
